import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteFieldCropPractice, deleteFieldCropPracticeAdmin, deleteFieldCropPracticeSimulations } from "services";

import { getEnvironment, invalidateCarbonProgramQueriesByChunk, invalidateQueriesByChunk } from "utils";

import {
  DELETE_FIELD_CROP_PRACTICE,
  GET_FARM_DATA_SUBMISSION_STATUS,
  GET_FIELDS_CROP_RECOMMENDATIONS,
  GET_FIELD_PRACTICES,
  GET_FIELD_PRACTICE_FILTER_OPTIONS,
} from "consts";

type MutateParams = {
  fieldId: string;
  simulationId?: string;
  cropId: string;
};

export const useDeleteFieldCropPractice = ({ fieldId, farmId, simulationId }: { fieldId: string; farmId?: string; simulationId?: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DELETE_FIELD_CROP_PRACTICE, fieldId, simulationId],
    mutationFn: ({ cropId }: { cropId: string }) => mutate({ cropId, simulationId, fieldId }),
    onSettled: () => {
      invalidateCarbonProgramQueriesByChunk(fieldId, simulationId);
      invalidateQueriesByChunk(GET_FIELD_PRACTICES, fieldId);
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICES, fieldId, simulationId ?? null] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_FILTER_OPTIONS] });
      if (!simulationId) {
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });
        queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
      } else {
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId], [simulationId]] });
      }
    },
  });
};

const mutate = ({ fieldId, simulationId, cropId }: MutateParams) => {
  const isAdmin = getEnvironment().admin;

  if (isAdmin) {
    return deleteFieldCropPracticeAdmin({ fieldId, cropId });
  }

  if (simulationId) {
    return deleteFieldCropPracticeSimulations({ fieldId, simulationId, cropId });
  }

  return deleteFieldCropPractice({ fieldId, cropId });
};
